import { useAppStore } from '../../contexts/store';
import { ThemeToggle } from '../common/ThemeToggle';
import { Menu, X, LogOut, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Button from '../common/Button';
import { sanitizeLogoUrl } from '../../utils/validation';

export function Header() {
  const { tenantConfig, isAuthenticated, logout, user } = useAppStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  // Sanitize logo URL to prevent SSRF and XSS
  const safeLogoUrl = tenantConfig?.logoUrl
    ? sanitizeLogoUrl(tenantConfig.logoUrl)
    : '/images/logo.jpg';

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Free Tools', href: '/tools' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <motion.header
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      className="bg-surface/80 backdrop-blur-xl border-b border-border/50 sticky top-0 z-50 shadow-sm"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.a
            href="/"
            className="flex items-center gap-3 group relative"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            {safeLogoUrl && (
              <img
                src={safeLogoUrl}
                alt={tenantConfig?.companyName || 'Packet Prospector'}
                className="h-12 w-auto transition-transform group-hover:scale-105"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            )}
            {!safeLogoUrl && (
              <span className="text-xl font-bold bg-gradient-to-r from-brand-purple-600 to-brand-teal-500 bg-clip-text text-transparent">
                {tenantConfig?.companyName || 'Packet Prospector'}
              </span>
            )}

            {/* Animated underline */}
            <motion.div
              className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-purple-600 to-brand-teal-500 origin-left"
              initial={{ scaleX: 0 }}
              whileHover={{ scaleX: 1 }}
              transition={{ duration: 0.3 }}
            />
          </motion.a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link, index) => (
              <motion.a
                key={link.href}
                href={link.href}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="relative group py-2"
              >
                <span className="text-sm font-medium text-text-secondary group-hover:text-text transition-colors duration-200">
                  {link.label}
                </span>
                {/* Animated underline */}
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-brand-purple-600 to-brand-teal-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </motion.a>
            ))}

            {isAuthenticated && (
              <motion.a
                href="/dashboard"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: navLinks.length * 0.1 }}
                className="relative group py-2"
              >
                <span className="text-sm font-medium text-text-secondary group-hover:text-text transition-colors duration-200">
                  Dashboard
                </span>
                {/* Animated underline */}
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-brand-purple-600 to-brand-teal-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </motion.a>
            )}

            {/* Theme Toggle with animation */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
              className="ml-2"
            >
              <ThemeToggle />
            </motion.div>

            {isAuthenticated ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="flex items-center gap-4 ml-4"
              >
                <span className="text-sm font-medium text-text-secondary">
                  {user?.firstName} {user?.lastName}
                </span>
                <Button variant="outline" size="sm" onClick={handleLogout} leftIcon={<LogOut size={16} />}>
                  Logout
                </Button>
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
                className="flex items-center gap-3 ml-4"
              >
                <a href="/login">
                  <Button variant="ghost" size="sm">
                    Login
                  </Button>
                </a>
                <a href="/register-isp">
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={<Sparkles size={16} />}
                      className="bg-gradient-to-r from-brand-purple-600 to-brand-purple-700 hover:from-brand-purple-700 hover:to-brand-purple-800 shadow-lg shadow-brand-purple-500/30"
                    >
                      Sign Up
                    </Button>
                  </motion.div>
                </a>
              </motion.div>
            )}
          </nav>

          {/* Mobile Menu Button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="md:hidden p-2 text-text hover:bg-surface rounded-lg transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.nav
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden py-4 border-t border-border bg-surface/50 backdrop-blur-sm overflow-hidden"
            >
              <div className="flex flex-col gap-1">
                {navLinks.map((link, index) => (
                  <motion.a
                    key={link.href}
                    href={link.href}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="text-text-secondary hover:text-text py-3 px-4 font-medium transition-colors duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </motion.a>
                ))}

                {isAuthenticated && (
                  <motion.a
                    href="/dashboard"
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: navLinks.length * 0.05 }}
                    className="text-text-secondary hover:text-text py-3 px-4 font-medium transition-colors duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </motion.a>
                )}

                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: (navLinks.length + 1) * 0.05 }}
                  className="flex items-center justify-between py-3 px-4"
                >
                  <span className="text-sm font-medium">Theme</span>
                  <ThemeToggle />
                </motion.div>

                {isAuthenticated ? (
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: (navLinks.length + 2) * 0.05 }}
                    className="px-4 pt-2"
                  >
                    <Button variant="outline" size="sm" onClick={handleLogout} className="w-full">
                      Logout
                    </Button>
                  </motion.div>
                ) : (
                  <motion.div
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: (navLinks.length + 2) * 0.05 }}
                    className="flex flex-col gap-2 px-4 pt-2"
                  >
                    <a href="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="ghost" size="sm" className="w-full">
                        Login
                      </Button>
                    </a>
                    <a href="/register-isp" onClick={() => setMobileMenuOpen(false)}>
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-full bg-gradient-to-r from-brand-purple-600 to-brand-purple-700 hover:from-brand-purple-700 hover:to-brand-purple-800"
                        leftIcon={<Sparkles size={16} />}
                      >
                        Sign Up
                      </Button>
                    </a>
                  </motion.div>
                )}
              </div>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
}
